import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { withTranslation } from "react-i18next";

function CustomRating({
  t,
  value,
  onChange,
  readOnly = false,
  size = 24,
  spacing = 6,
  allowHalfStars = true,
}) {
  const [selectedValue, setSelectedValue] = useState(0);
  const [hoverValue, setHoverValue] = useState(null);
  const containerRef = useRef(null);

  // Keep internal state in sync when `value` prop changes
  useEffect(() => {
    if (typeof value === "number" && value >= 0 && value <= 5) {
      setSelectedValue(value);
    }
  }, [value]);

  const effectiveValue = hoverValue != null ? hoverValue : selectedValue;

  const computeFillPercentForStar = useCallback(
    (starIndex) => {
      const starValue = starIndex + 1; // 1..5
      if (effectiveValue >= starValue) return 100;
      if (allowHalfStars && effectiveValue >= starValue - 0.5) return 50;
      return 0;
    },
    [effectiveValue, allowHalfStars]
  );

  const handleMouseMove = useCallback(
    (event, index) => {
      if (readOnly) return;
      const target = event.currentTarget;
      const rect = target.getBoundingClientRect();
      const offsetX = event.clientX - rect.left;
      const half = rect.width / 2;
      const isLeftHalf = offsetX <= half;
      const tentative = allowHalfStars && isLeftHalf ? index + 0.5 : index + 1;
      setHoverValue(tentative);
    },
    [readOnly, allowHalfStars]
  );

  const handleMouseLeave = useCallback(() => {
    if (readOnly) return;
    setHoverValue(null);
  }, [readOnly]);

  const handleClick = useCallback(
    (event, index) => {
      if (readOnly) return;
      const target = event.currentTarget;
      const rect = target.getBoundingClientRect();
      const offsetX = event.clientX - rect.left;
      const half = rect.width / 2;
      const isLeftHalf = offsetX <= half;
      const next = allowHalfStars && isLeftHalf ? index + 0.5 : index + 1;
      setSelectedValue(next);
      if (typeof onChange === "function") onChange(next);
    },
    [onChange, readOnly, allowHalfStars]
  );

  const stars = useMemo(() => new Array(5).fill(0), []);
  const uid = useMemo(() => Math.random().toString(36).slice(2, 9), []);

  const starStyle = useMemo(
    () => ({
      position: "relative",
      display: "inline-block",
      width: size,
      height: size,
      lineHeight: `${size}px`,
      fontSize: size,
      cursor: readOnly ? "default" : "pointer",
      userSelect: "none",
    }),
    [size, readOnly]
  );

  return (
    <div
      ref={containerRef}
      style={{ display: "flex", alignItems: "center", gap: spacing }}
      onMouseLeave={handleMouseLeave}
      aria-label={t ? t("Rating") : "Rating"}
      role="img"
    >
      {stars.map((_, index) => {
        const percent = computeFillPercentForStar(index);
        return (
          <span
            key={index}
            style={starStyle}
            onMouseMove={(e) => handleMouseMove(e, index)}
            onClick={(e) => handleClick(e, index)}
            aria-hidden
          >
            <svg
              width={size}
              height={size}
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              style={{ display: "block" }}
            >
              <defs>
                <clipPath id={`clip-${uid}-${index}`}>
                  <rect x="0" y="0" width={(percent / 100) * 24} height="24" />
                </clipPath>
              </defs>
              {/* Base star (gray) */}
              <path
                d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
                fill="#c6c9d2"
              />
              {/* Filled star (yellow) clipped to percentage */}
              <g clipPath={`url(#clip-${uid}-${index})`}>
                <path
                  d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
                  fill="#FFC107"
                />
              </g>
            </svg>
          </span>
        );
      })}
    </div>
  );
}

export default withTranslation()(CustomRating);
